import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import { OMNICHANNEL_MESSAGES_QUERY_KEY } from '@/api/queries/omnichannel/key';
import type {
  OmnichannelMessage,
  OmnichannelNewMessageEvent,
  PaginatedMessagesResponse,
} from '@/types';

export const realtimeEventToMessage = (
  event: OmnichannelNewMessageEvent
): OmnichannelMessage => {
  const senderId = event.message.senderId ?? 'customer';
  const created = event.message.createdAt ?? new Date().toISOString();
  return {
    id: event.message.id,
    message: event.message.content,
    content: event.message.content,
    messageType: event.message.messageType,
    senderId,
    createdAt: created,
    from: {
      id: senderId,
      name: senderId,
      email: '',
    },
    to: { data: [] },
    created_time: created,
  };
};

/** Prepends a realtime message onto the latest messages page when that thread is open. */
export const appendMessageToMessagesCache = (
  queryClient: QueryClient,
  event: OmnichannelNewMessageEvent
) => {
  const conversationId = String(event.conversationId);
  const channel = event.channel;
  const incoming = realtimeEventToMessage(event);

  const queries = queryClient.getQueriesData<
    InfiniteData<PaginatedMessagesResponse>
  >({
    queryKey: [OMNICHANNEL_MESSAGES_QUERY_KEY, conversationId],
  });

  const norm = (c: unknown) => (typeof c === 'string' ? c.toLowerCase() : '');

  for (const [queryKey, data] of queries) {
    if (!data?.pages?.length) continue;

    const keyChannel = queryKey[2];
    if (keyChannel && channel && norm(keyChannel) !== norm(channel)) continue;

    queryClient.setQueryData<InfiniteData<PaginatedMessagesResponse>>(
      queryKey,
      {
        ...data,
        pages: data.pages.map((page, index) => {
          if (index !== data.pages.length - 1) return page;
          const exists = page.data.some((m) => m.id === incoming.id);
          if (exists) return page;
          return {
            ...page,
            data: [...page.data, incoming],
          };
        }),
      }
    );
  }
};
